/* SPDX-FileCopyrightText: 2014-present Kriasoft */
/* SPDX-License-Identifier: MIT */

import EmailIcon from "@mui/icons-material/Email";
import { Box, Button, ButtonProps } from "@mui/joy";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SignInMethod } from "../core/auth";
import { GoogleIcon } from "../icons";
import { RecommendedBadge } from "./RecommendedBadge";

const GOOGLE_CLIENT_ID = process.env.GOOGLE_CLIENT_ID;

// Add Google API types
declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: Record<string, unknown>) => {
            requestAccessToken: () => void;
          };
        };
      };
    };
  }
}

export function LoginButton(props: LoginButtonProps): JSX.Element {
  const {
    signInMethod,
    onLoginSuccess,
    onLoginError,
    showRecommendedBadge = true,
    ...other
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const icon =
    signInMethod === "google.com" ? (
      <GoogleIcon sx={{ color: "inherit" }} />
    ) : signInMethod === "email" ? (
      <EmailIcon sx={{ color: "inherit" }} />
    ) : signInMethod === "anonymous" ? null : null;

  const handleLoginClick = () => {
    if (signInMethod === "email") {
      onLoginSuccess?.({});
      return;
    }
    if (signInMethod !== "google.com") {
      onLoginSuccess?.({});
      return;
    }
    if (window.google?.accounts?.oauth2) {
      setIsLoading(true);
      window.google.accounts.oauth2
        .initTokenClient({
          client_id: GOOGLE_CLIENT_ID!,
          callback: handleCredentialResponse,
          scope: "email profile",
        })
        .requestAccessToken();
    } else {
      console.error("Google OAuth not initialized");
    }
  };

  const handleCredentialResponse = useCallback(
    async (response: { access_token: string }) => {
      if (response.access_token) {
        try {
          const userInfoResponse = await fetch(
            "https://www.googleapis.com/oauth2/v3/userinfo",
            {
              headers: {
                Authorization: `Bearer ${response.access_token}`,
              },
            },
          );
          const userInfo = await userInfoResponse.json();

          const loginResponse = await fetch("/api/auth/google/callback", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              token: response.access_token,
              userInfo: userInfo,
            }),
            // credentials: 'include'
          });

          const data = await loginResponse.json();
          if (data.user_id) {
            onLoginSuccess?.(data.user);
          } else {
            throw new Error(data.error || "Login failed");
          }
        } catch (error) {
          console.error("Login failed:", error);
          onLoginError?.(error);
        } finally {
          setIsLoading(false);
        }
      }
    },
    [onLoginSuccess, onLoginError],
  );

  useEffect(() => {
    const initializeGoogleLogin = () => {
      if (window.google?.accounts?.oauth2) {
        window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID!,
          callback: handleCredentialResponse,
          scope: "openid email profile",
          ux_mode: "popup",
        });
      }
    };

    if (typeof window.google !== "undefined") {
      initializeGoogleLogin();
    } else {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleLogin;
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, [handleCredentialResponse]);

  // Render Google login button with recommended badge
  if (signInMethod === "google.com") {
    return (
      <Box sx={{ position: "relative" }}>
        <Button
          startDecorator={icon}
          variant="soft"
          onClick={handleLoginClick}
          loading={isLoading}
          color="primary"
          fullWidth
          sx={{
            "&:active": {
              bgcolor: "primary.solidBg",
              transform: "scale(0.98)",
            },
          }}
          {...other}
        >
          {t("auth.loginWithGoogle")}
        </Button>
        <RecommendedBadge show={showRecommendedBadge} />
      </Box>
    );
  }

  // Render other login buttons
  return (
    <Button
      startDecorator={icon}
      variant={signInMethod === "anonymous" ? "plain" : "soft"}
      onClick={handleLoginClick}
      loading={isLoading}
      color={signInMethod === "anonymous" ? "neutral" : "primary"}
      sx={{
        ...(signInMethod === "anonymous"
          ? {
              color: "neutral.500",
              textDecoration: "underline",
              fontSize: "xs",
              padding: 0,
              "&:hover": {
                bgcolor: "neutral.softBg",
              },
            }
          : {
              "&:active": {
                bgcolor: "primary.solidBg",
                transform: "scale(0.98)",
              },
            }),
      }}
      {...other}
    >
      {signInMethod === "email"
        ? t("auth.loginWithEmail")
        : signInMethod === "anonymous"
          ? t("auth.back")
          : t("auth.unknown")}
    </Button>
  );
}

export type LoginButtonProps = Omit<
  ButtonProps<
    "button",
    {
      signInMethod: SignInMethod;
      onLoginSuccess?: (userData: Record<string, unknown>) => void;
      onLoginError?: (error: unknown) => void;
      showRecommendedBadge?: boolean;
    }
  >,
  "children"
>;
